import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Leaf, Heart, Brain, Dumbbell, Apple, Moon,
  X, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from "../ui/button";
import '../styles/fonts.css';

const services = [
  {
    id: 1,
    icon: Dumbbell,
    title: 'Restorative Therapy Program',
    shortDesc: 'Our restorative therapy program is a patient-centered approach to help individuals regain or maintain their maximum level of physical and functional independence after illness or injury. ',
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-50',
    fullDesc: 'Our goal is to empower patients to do as much as possible for themselves by focusing on individual goals through designed program plans.',
    benefits: [
      'A program designed for people whose strength is entering a lower level and where muscle mass is decreasing.',
      'Can be applied for seniors in their own home, living in Retirement home, or for those entering or at Long Term Care Facilities.',
      'Provides Seniors with their best functional level and quality of life.',
      'Offers a Holistic View of a person (Physical, Intellectual, Emotional, Social, and Spiritual).',
      'Keeps a person a person, gives back HOPE.',
      'Maintains opportunities to be INDEPENDENT.'
    ],
    duration: '60-90 minutes',
    price: 'From $120/session'
  },
  {
    id: 2,
    icon: Heart,
    title: 'Restorative Nursing Care',
    shortDesc: 'Our Restorative Nursing Care includes individualized care plans, therapeutic activities, functional training, toileting and communication programs, and disability support.',
    color: 'from-rose-500 to-pink-500',
    bgColor: 'bg-rose-50',
    fullDesc: 'Help and educate seniors restore, enhance, prevent decline, or maintain optimal physical function.',
    benefits: [
      'Assisting seniors in performing restorative exercises aimed at enhancing mobility, strength, flexibility, and promoting optimal physical function.',
      'Assisting with Daily Activities – Our certified Restorative staff can assist seniors with activities of daily living (ADLs) such as bathing, activities, transfers, toileting, eating, and dressing to enhance comfort and quality of life.',
      'Ongoing Assessments, Documentations and Evaluation.'
    ],
    duration: '45-60 minutes',
    price: 'From $95/session'
  },
];

export default function ServicesSection() {
  const [selectedService, setSelectedService] = useState(null);

  const scrollToContact = () => {
    setSelectedService(null);
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <section id="about-us" className="py-24 bg-purple">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="text-center mb-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-emerald-600 font-medium text-sm uppercase tracking-wider">WHO WE ARE</span>
          <h2 className="kaushan-script text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Skilled Team of <span className="text-emerald-600">Care Associates</span>
          </h2>
          <p className="fuzzy-bubbles text-left text-grey-500 text-sm md:text-lg font-medium max-w-5xl mx-auto leading-relaxed">
            Restorative Connections stands out for its unique holistic approach to senior care. Interdisciplinary team
            of certified professionals, including RNs, RPNs, PSWs and Certified Restorative Aide who collaborate to deliver
            personalized therapy and support. We integrate restorative exercises, daily living assistance, restorative dining,
            social engagement and comprehensive care planning. All guided by a commitment to compassion, excellence and empowerment
            of everyone to achieve their best quality of life.
            <br /><br />
            <p class="text-purple-500 text-center text-relaxed font-medium"><strong> Experience in Quality Healthcare goes a Very Long Way…</strong></p>
            <p class="text-amber-600 text-center text-relaxed font-medium"><strong>A combined 50+ years of Industry Experience in Various Areas of Nursing…</strong></p>    
          </p>
        </motion.div>
           <span className="inline-block bg-gray/90 backdrop-blur-md rounded-full text-purple/10 caveat-ts font-meduim mb-6 border border-white/20">
            ✨ Restorative Connections offers:
          </span>
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className={`group relative ${service.bgColor} rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all duration-500 border border-gray-100 overflow-hidden`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => setSelectedService(service)}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${service.color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500`} />

              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${service.color} mb-4`}>
                <service.icon className="w-6 h-6 text-white" />
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
              <p className="text-gray-600 mb-4">{service.shortDesc}</p>

              <div className="flex items-center text-emerald-600 font-medium group-hover:gap-2 transition-all">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedService(null)}
          >
            <motion.div
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`relative p-8 bg-gradient-to-br ${selectedService.color} text-white rounded-t-3xl`}>
                <button
                  onClick={() => setSelectedService(null)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="inline-flex p-4 rounded-2xl bg-white/20 mb-4">
                  <selectedService.icon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold mb-2">{selectedService.title}</h3>
                {/* <div className="flex flex-wrap gap-4 text-white/90 text-sm"> */}
                  {/* <span>⏱ {selectedService.duration}</span> */}
                  {/* <span>💰 {selectedService.price}</span> */}
                {/* </div> */}
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {selectedService.fullDesc}
                </p>

                <h4 className="font-semibold text-gray-900 mb-4">What's Included:</h4>
                <ul className="space-y-3 mb-8">
                  {selectedService.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={scrollToContact}
                    className={`flex-1 h-12 bg-gradient-to-r ${selectedService.color} hover:opacity-90 text-white font-semibold rounded-xl`}
                  >
                    Book a Consultation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedService(null)}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}